import { Fragment } from 'react'

/*
  A board member's words, as written.

  Members reach for markdown emphasis whatever the prompt says, and a two-voice consult
  reliably produces "**Dana Reyes:** ..." because the instruction asks each voice to be
  labelled. Rendered as plain text the owner reads literal asterisks around his chair's
  name, which looks like the product leaked something.

  Deliberately only bold. This is not a markdown renderer and should not become one: the
  board writes prose, and the one construction it actually uses is the label. Anything
  else stays exactly as the member typed it, which is the safe direction to fail in when
  the text is model output being put on a page.
*/

const BOLD = /\*\*([^*\n]+)\*\*/g

/*
  Both callers render into .msg__bubble, which is white-space: pre-wrap, and that is
  deliberate: a member's paragraph breaks are theirs, and collapsing them would run four
  separate arguments into one block.

  The cost is that every other piece of whitespace is honoured too, and model output is not
  clean. One escalated consult arrived with about fifty spaces in front of "This isn't a
  close call" and four blank lines before its last paragraph, so the bubble drew two
  paragraphs starting halfway across the card with a hole above one of them.

  So leading and trailing horizontal space comes off each line, and a run of blank lines
  becomes one. What survives is the breaks the member actually meant. Indentation is never
  meaningful in here, because nothing in a bubble is code or a table.
*/
function tidy(text: string): string {
  return text
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function Prose({ raw }: { raw: string }) {
  const text = tidy(raw)

  if (!text.includes('**')) return <>{text}</>

  const parts: React.ReactNode[] = []
  let at = 0

  for (const match of text.matchAll(BOLD)) {
    const start = match.index
    if (start > at) parts.push(text.slice(at, start))
    parts.push(<strong className="prose__b" key={start}>{match[1]}</strong>)
    at = start + match[0].length
  }

  if (at < text.length) parts.push(text.slice(at))

  return <>{parts.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</>
}
